import styled, { css } from 'vue-styled-components';
import {
    COLORS,
    BLUE,
    GRAY,
    square,
    applyHoverAndPress,
} from '@/styles/common.styled';
import {
    FLEX_COLUMN_DIV,
    FLEX_VERTICAL_CENTER_DIV,
} from '@/styles/common.styled.component';
import * as C from '@/components/Dashboard/common/Common.styled';

/**
 * @description use hide when you do not want to render component while using second panel
 */
export const ComponentWrapper = styled('div', {
    hide: Boolean,
    hasContent: Boolean,
})`
    ${({ hide }) => hide && `display: none;`}

    .option-title {
        ${({ hasContent = true }) => !hasContent && `border-bottom: unset;`}
    }
`;

export const ListWrapper = styled(FLEX_COLUMN_DIV, {
    selected: Boolean,
    isHoverPointer: Boolean,
    disabled: Boolean,
})`
    border-radius: 2px;
    background-color: #f4f6f8;
    border: 1px solid ${({ selected }) => (selected ? BLUE(60) : GRAY(20))};
    position: relative;
    ${({ selected }) =>
        selected &&
        css`
            box-shadow: 0 0 10px 0 rgba(0, 84, 198, 0.15);
        `}
    .check-box {
        svg {
            top: 0;
        }
    }

    .drag-area {
        display: flex;
        flex-wrap: wrap;
        height: 100%;
        margin-top: -8px;
    }

    ${({ isHoverPointer }) =>
        isHoverPointer &&
        css`
            &:hover {
                cursor: pointer;
            }
        `}

    .dashboard-filter-tag > p {
        white-space: initial;
        max-width: initial;
    }

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
            & > div {
                background-color: #ebeef0;
            }
            & div,
            & svg {
                cursor: not-allowed;
                &:hover {
                    cursor: not-allowed;
                }
            }
        `}
`;

export const ItemWrapper = styled('div', {
    disabled: Boolean,
    hasScroll: Boolean,
    height: Number,
})`
    background: ${({ disabled }) => (disabled ? GRAY(10) : '#fff')};
    padding: 8px 12px;
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${COLORS['gray-25']};
    }
    & > div.flex-wrapper {
        & .table-item {
            &:nth-child(2n + 1) {
                margin-right: 8px;
            }
        }
    }

    ${({ hasScroll }) =>
        hasScroll &&
        css`
            overflow-y: auto;
            max-height: 150px;
            position: relative;
            & .drag-area {
                height: auto;
            }
        `}

    ${({ height }) =>
        height &&
        css`
            max-height: ${height}px;
        `}
`;

export const ItemBox = styled(FLEX_VERTICAL_CENTER_DIV, {
    dragCursor: String,
    width: String,
})`
    background-color: ${BLUE(10)};
    padding: 3px 10px;
    font-size: 12px;
    border-radius: 12px;
    margin-top: 8px;
    margin-right: 4px;
    width: ${({ width = 'auto' }) => width};

    .text-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    & .item-text {
        height: auto;
        font-size: 14px;
        text-align: left;
        word-break: break-word;
        margin: 0 4px;
        color: ${BLUE(60)};
        -webkit-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    & > p {
        -webkit-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    & .move {
        fill: ${GRAY(40)};
        margin-right: 4px;
    }

    & svg {
        ${square(12)};
        min-width: 12px;
    }

    & .share-icon {
        fill: ${BLUE(60)};
        cursor: pointer;
    }

    & .edit-icon,
    & .share-icon {
        margin-right: 4px;
    }

    & .close {
        ${square(14)};
        fill: var(--color-gray-100);
        cursor: pointer;
    }

    ${({ dragCursor }) =>
        dragCursor &&
        css`
            cursor: ${dragCursor};
        `};
`;

export const TitleLabel = styled(FLEX_VERTICAL_CENTER_DIV, {
    disabled: Boolean,
    hideBorder: Boolean,
})`
    font-size: 14px;
    font-weight: 500;

    ${({ hideBorder }) => {
        return !hideBorder && `border-bottom: 1px solid ${COLORS['gray-20']};`;
    }}

    border-radius: 2px 2px 0 0;
    padding: 11px 16px;
    background: ${({ disabled }) =>
        disabled ? 'rgba(8, 10, 13, 0.05)' : GRAY(10)};
    justify-content: space-between;
    color: ${GRAY(100)};
`;

export const IconWrapper = styled(FLEX_VERTICAL_CENTER_DIV)`
    color: ${GRAY(100)};
    & svg {
        ${square(16)};
        &.plus-icon {
            margin-right: 4px;
        }
        &.metric-icon,
        &.select-icon {
            margin-right: 8px;
        }
    }
`;

export const EditButton = styled(FLEX_VERTICAL_CENTER_DIV, {
    highlighted: Boolean,
})`
    flex-shrink: 0;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;

    color: ${({ highlighted }) =>
        highlighted ? 'var(--color-blue-70)' : 'var(--color-gray-40)'};
`;

export const InfoIconWrapper = styled(IconWrapper)`
    margin-left: 8px;
    margin-right: auto;

    svg {
        fill: var(--color-gray-70);
    }
`;

export const NoListSpan = styled.span`
    color: ${GRAY(40)};
    font-size: 13px;
`;

export const ContentWrapper = styled(FLEX_COLUMN_DIV)`
    height: 100%;
    .dummy {
        height: 20px;
        width: 100%;
        flex-shrink: 0;
        margin-top: -20px;
        z-index: 1;
        position: sticky;
        top: -20px;
        background-color: #fff;
    }
`;

export const ContentGuideWrapper = styled(FLEX_COLUMN_DIV)`
    margin-bottom: 16px;
    & span {
        color: ${GRAY(100)};
    }
    & .main-guide {
        font-size: 14px;
        font-weight: 500;
    }

    & .sub-guide {
        margin-top: 8px;
        color: ${GRAY(50)};
    }
`;

export const DropdownSearchLabel = styled(C.DropdownSearchLabel)`
    margin: 0 0 13px 0;
    width: 100%;
    height: 40px;
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #fff;

    .search-input {
        width: 100%;
    }
`;

export const BreakdownBtn = styled('button', { lang: String })`
    border-radius: 2px;
    border: 1px solid ${GRAY(30)};
    padding: 10px;
    color: ${GRAY(80)};
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    min-width: ${({ lang }) => (lang === 'ko' ? '120px' : '180px')};
    flex-shrink: 0;
    height: 100%;
    ${applyHoverAndPress('white', 'background-color')};
    & svg {
        ${square(15)};
        fill: ${GRAY(100)};
        margin-right: 4px;
    }
`;

export const TitleWrapper = styled(FLEX_VERTICAL_CENTER_DIV)`
    gap: 6px;

    .info-line-icon {
        width: 24px;
        height: 24px;
    }
`;

export const ITEM_STATUS = {
    NORMAL: 'normal',
    INVALID: 'invalid',
    LOADING: 'loading',
    CANDIDATE: 'candidate',
};

const ITEM_STATUS_STYLES = {
    [ITEM_STATUS.NORMAL]: {
        bg: BLUE(10),
        text: BLUE(60),
        border: 'unset',
    },
    [ITEM_STATUS.INVALID]: {
        bg: 'var(--color-gray-20)',
        text: 'var(--color-gray-50)',
        border: 'unset',
    },
    [ITEM_STATUS.LOADING]: {
        bg: BLUE(10),
        text: BLUE(60),
        border: 'unset',
    },
};

const getItemStatusStyle = (status) =>
    ITEM_STATUS_STYLES[status] || ITEM_STATUS_STYLES[ITEM_STATUS.NORMAL];

export const StatusItemBox = styled(ItemBox, {
    status: String,
    loading: Boolean,
})`
    background-color: ${({ status }) => getItemStatusStyle(status).bg};
    border: ${({ status }) => getItemStatusStyle(status).border};

    & .item-text {
        color: ${({ status }) => getItemStatusStyle(status).text};
    }

    ${({ loading }) =>
        loading &&
        css`
            @keyframes shimmer {
                0% {
                    transform: translateX(-100%);
                }
                100% {
                    transform: translateX(100%);
                }
            }

            min-width: 80px;
            position: relative;
            overflow: hidden;

            &::after {
                content: '';
                position: absolute;
                inset: 0;
                background: linear-gradient(
                    90deg,
                    transparent 0%,
                    rgba(255, 255, 255, 0.4) 50%,
                    transparent 100%
                );
                animation: shimmer 1s ease-in-out infinite;
                will-change: transform;
            }
        `}
`;
